// Punktewertung (0–100), relativ gerechnet und dadurch über Branchen vergleichbar.
import { applicableActions, emissions } from "./engine.js"
import { summarize } from "./baseline.js"

export const SBTI = { scope12PerYear: 0.042, scope3PerYear: 0.025, baseYear: 2025 }
export const WEIGHTS = { reduction: 60, efficiency: 20, guess: 10, events: 10 }

const bestCache = new Map()

// Beste erreichbare Reduktion mit dem Grundbudget, per Vollaufzählung aller Kombinationen.
export function bestPlay(data, state) {
  if (bestCache.has(state.industry)) return bestCache.get(state.industry)
  const ids = applicableActions(data, state)
  const budget = data.actions.budget_per_round * data.actions.rounds.length
  const reference = { ...state, owned: {}, eventResults: [] }
  const base = summarize(data, state.baseSources).total
  let best = { reduction: 0, spent: 0, owned: [] }

  for (let mask = 1; mask < 1 << ids.length; mask++) {
    let cost = 0
    const owned = {}
    for (let i = 0; i < ids.length; i++) {
      if (mask & (1 << i)) {
        cost += data.actions.actions[ids[i]].cost
        owned[ids[i]] = 0
      }
    }
    if (cost > budget) continue
    const reduction = 1 - emissions(data, reference, owned).total / base
    if (reduction > best.reduction + 1e-9 || (Math.abs(reduction - best.reduction) < 1e-9 && cost < best.spent)) {
      best = { reduction, spent: cost, owned: Object.keys(owned) }
    }
  }
  bestCache.set(state.industry, best)
  return best
}

export function result(data, state) {
  const base = summarize(data, state.baseSources)
  const final = emissions(data, state)
  const reduction = 1 - final.total / base.total
  const scope12Reduction = base.scope12 > 0 ? 1 - final.scope12 / base.scope12 : 0
  const scope3Reduction = base.scope3 > 0 ? 1 - final.scope3 / base.scope3 : 0
  const years = data.actions.rounds.at(-1) - SBTI.baseYear
  const sbti = {
    scope12Target: SBTI.scope12PerYear * years,
    scope3Target: SBTI.scope3PerYear * years,
    scope12OnTrack: scope12Reduction >= SBTI.scope12PerYear * years,
    scope3OnTrack: scope3Reduction >= SBTI.scope3PerYear * years
  }

  const best = bestPlay(data, state)
  const clamp = (value) => Math.max(0, Math.min(1, value))
  const savedPerPoint = state.spent > 0 ? reduction / state.spent : 0
  const bestPerPoint = best.spent > 0 ? best.reduction / best.spent : 1
  const actualSharePercent = base.scope3Share * 100
  const guessError = state.guess === null ? 100 : Math.abs(state.guess - actualSharePercent)
  const successes = state.eventResults.filter((event) => event.success).length

  const points = {
    reduction: Math.round(WEIGHTS.reduction * clamp(best.reduction > 0 ? reduction / best.reduction : 0)),
    efficiency: Math.round(WEIGHTS.efficiency * clamp(savedPerPoint / bestPerPoint)),
    guess: Math.round(WEIGHTS.guess * clamp(1 - guessError / 40)),
    events: Math.round((WEIGHTS.events * successes) / data.events.pools.length)
  }
  const score = Object.values(points).reduce((sum, value) => sum + value, 0)

  return {
    industry: state.industry,
    employees: state.employees,
    guess: state.guess,
    baseline: { total: base.total, scope12: base.scope12, scope3: base.scope3, scope3Share: base.scope3Share, stations: base.stations },
    final: { total: final.total, scope12: final.scope12, scope3: final.scope3, stations: final.stations },
    reduction,
    scope12Reduction,
    scope3Reduction,
    sbti,
    actions: Object.entries(state.owned).map(([id, round]) => ({ id, round })),
    events: state.eventResults,
    spent: state.spent,
    best: { reduction: best.reduction, actions: best.owned },
    points,
    score: Math.min(100, score)
  }
}
